import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, CardContent, CardHeader, CardTitle, Input, PasswordInput } from '@mlm/ui';
import { Header } from '../storefront/components/header';
import { Footer } from '../storefront/components/footer';
import { login } from '../lib/api';
import { useAuthStore } from '../lib/auth-store';
import { resolveLoginDestination } from '../lib/login-resume';
import { queryClient } from '../lib/query-client';

export function LoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const setSession = useAuthStore((s) => s.setSession);
  const [identifier, setIdentifier] = useState('');
  const [password, setPassword] = useState('');
  const [totp, setTotp] = useState('');
  const [needsTotp, setNeedsTotp] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await login({ identifier, password, totp: totp || undefined });
      setSession(res.accessToken, res.user);
      // One-shot virtual-checkout resume (flag-gated; `?next=` validation
      // included) — see lib/login-resume.ts. Any lookup failure inside degrades
      // to the plain next/dashboard landing, so login itself can't get stuck.
      navigate(await resolveLoginDestination(queryClient, params.get('next')));
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: string } } })?.response?.data?.error;
      if (msg === 'totp_required') {
        setNeedsTotp(true);
        setError('TOTP code required');
      } else if (msg === 'cashier_not_allowed_in_member_portal') {
        setError('Cashier accounts cannot use the member portal. Please use the POS app.');
      } else {
        setError('Invalid email or password');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="omni-store flex min-h-screen flex-col bg-[#f5f5f5]">
      <Header />
      <main className="flex flex-1 flex-col items-center justify-center px-4 py-12">
        <Card className="w-full max-w-sm overflow-hidden">
          {/* 2px orange hairline at the card's top edge — matches the Omni
            storefront brand (the shared green/blue .omni-hairline is kept for
            admin/cashier). */}
          <div className="h-0.5" style={{ background: 'linear-gradient(90deg,#EE4D2D,#FF8A5C)' }} />
          <CardHeader>
            <CardTitle>Member Sign in</CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium">Email or username</label>
                <Input
                  type="text"
                  value={identifier}
                  onChange={(e) => setIdentifier(e.target.value)}
                  autoComplete="username"
                  required
                />
              </div>
              <div className="space-y-2">
                <label className="text-sm font-medium">Password</label>
                <PasswordInput
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                />
              </div>
              {needsTotp && (
                <div className="space-y-2">
                  <label className="text-sm font-medium">TOTP code</label>
                  <Input
                    type="text"
                    inputMode="numeric"
                    value={totp}
                    onChange={(e) => setTotp(e.target.value)}
                    pattern="\d{6}"
                    required
                  />
                </div>
              )}
              {error && <p className="text-sm text-destructive">{error}</p>}
              <Button type="submit" disabled={submitting} className="w-full">
                {submitting ? 'Signing in…' : 'Sign in'}
              </Button>
              <p className="text-xs text-center text-muted-foreground">
                New here?{' '}
                <a className="underline" href="/signup">
                  Create an account
                </a>
              </p>
            </form>
          </CardContent>
        </Card>
      </main>
      <Footer />
    </div>
  );
}
